import { create } from 'zustand';
import { DEG2RAD } from 'three/src/math/MathUtils';
import { OrbitControls } from 'three-stdlib';
type Source = 'primary' | 'secondary' | null;

export type LookDirection = 'left' | 'right' | 'front' | 'back' | 'top' | null;

type CameraState = {
  isSynced: boolean;
  toggleSynced: () => void;
  setSynced: (isSynced: boolean) => void;
  isUpdating: boolean;
  primaryControls: OrbitControls | null;
  secondaryControls: OrbitControls | null;
  selectedLookDirection: LookDirection;
  setControls: (controls: OrbitControls | null, source: Source) => void;
  look: (direction: LookDirection) => void;
  isLocked: boolean;
  toggleLock: () => void;
};

export const useCameraStore = create<CameraState>((set, get) => {
  const handleLook = (direction: LookDirection) => {
    set({ selectedLookDirection: direction });
    let azimuthalAngle = 0;
    let polarAngle = 0;

    switch (direction) {
      case 'left':
        azimuthalAngle = -90;
        polarAngle = 90;
        break;
      case 'right':
        azimuthalAngle = 90;
        polarAngle = 90;
        break;
      case 'front':
        azimuthalAngle = 180;
        polarAngle = 90;
        break;
      case 'back':
        azimuthalAngle = 0;
        polarAngle = 90;
        break;
      case 'top':
        azimuthalAngle = 0;
        polarAngle = 0;
        break;
      default:
        azimuthalAngle = 0;
        polarAngle = 0;
        break;
    }

    const primaryControls = get().primaryControls;

    if (!primaryControls) {
      return;
    }

    primaryControls.setAzimuthalAngle(azimuthalAngle * DEG2RAD);
    primaryControls.setPolarAngle(polarAngle * DEG2RAD);

    primaryControls.update();

    const secondaryControls = get().secondaryControls;
    const isSynced = get().isSynced;

    if (secondaryControls) {
      if (isSynced) {
        // do nothing if synced, the secondary controls will be updated by the primary controls
        return;
      }
      secondaryControls.setAzimuthalAngle(azimuthalAngle * DEG2RAD);
      secondaryControls.setPolarAngle(polarAngle * DEG2RAD);
      secondaryControls.update();
    }
  };

  const handleLock = () => {
    const controls = get().primaryControls;
    if (!controls) {
      return;
    }

    const currentDirection = get().selectedLookDirection;
    if (!currentDirection) {
      return;
    }
    set({ isLocked: true });

    handleLook(currentDirection);

    controls.domElement?.addEventListener('mouseup', handleMouseUp);
    controls.domElement?.addEventListener('touchend', handleMouseUp);

    const secondaryControls = get().secondaryControls;
    if (secondaryControls) {
      secondaryControls.domElement?.addEventListener('mouseup', handleMouseUp);
      secondaryControls.domElement?.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      controls.domElement?.removeEventListener('mouseup', handleMouseUp);
      controls.domElement?.removeEventListener('touchend', handleMouseUp);
      secondaryControls?.domElement?.removeEventListener(
        'mouseup',
        handleMouseUp
      );
      secondaryControls?.domElement?.removeEventListener(
        'touchend',
        handleMouseUp
      );
    };
  };

  const handleMouseUp = () => {
    const prevLook = get().selectedLookDirection;
    if (!prevLook) {
      return;
    }

    // Use requestAnimationFrame to ensure smooth transition
    requestAnimationFrame(() => {
      handleLook(prevLook);
    });
  };

  const handleUnlock = () => {
    set({ isLocked: false });
    const controls = get().primaryControls;
    if (!controls) {
      return;
    }

    controls.domElement?.removeEventListener('mouseup', handleMouseUp);
    controls.domElement?.removeEventListener('touchend', handleMouseUp);

    const secondaryControls = get().secondaryControls;
    if (secondaryControls) {
      secondaryControls.domElement?.removeEventListener(
        'mouseup',
        handleMouseUp
      );
      secondaryControls.domElement?.removeEventListener(
        'touchend',
        handleMouseUp
      );
    }
  };

  const handleChange = (_e: any, source: Source) => {
    const { isSynced, isUpdating, primaryControls, secondaryControls } = get();
    if (isSynced === false) {
      return;
    }

    if (isUpdating === true) {
      return;
    }

    try {
      set({ isUpdating: true });

      const ctrl1 = primaryControls;
      const ctrl2 = secondaryControls;

      if (!ctrl1 || !ctrl2) return;
      const camera2 = ctrl2.object;
      const camera1 = ctrl1.object;

      // make absolutely sure that both cameras are initialized
      if (!camera1.isCamera || !camera2.isCamera) {
        return;
      }

      if (source === 'primary' && ctrl1 && ctrl2) {
        syncControl(ctrl1, ctrl2);
      }

      if (source === 'secondary' && ctrl1 && ctrl2) {
        syncControl(ctrl2, ctrl1);
      }
    } catch (error) {
      console.error(error);
    } finally {
      set({ isUpdating: false });
    }
  };

  const syncControl = (
    sourceControls: OrbitControls,
    targetControls: OrbitControls
  ) => {
    const sourceCamera = sourceControls.object;
    targetControls.object.position.copy(sourceCamera.position);
    targetControls.target.copy(sourceControls.target);
    targetControls.object.quaternion.copy(sourceCamera.quaternion);
    targetControls.object.zoom = sourceCamera.zoom;
    targetControls.update();
  };

  return {
    isSynced: false,
    isUpdating: false,
    setSynced: (isSynced: boolean) => set({ isSynced }),
    toggleSynced: () => set((state) => ({ isSynced: !state.isSynced })),
    primaryControls: null,
    secondaryControls: null,
    setControls: (controls, source) => {
      if (!controls) {
        return;
      }
      if (source === 'primary') {
        set({ primaryControls: controls });
        controls.addEventListener('change', (e) => {
          handleChange(e, source);
        });
      } else {
        set({ secondaryControls: controls });
        controls.addEventListener('change', (e) => {
          handleChange(e, source);
        });
      }
    },
    selectedLookDirection: null,
    look: handleLook,
    isLocked: false,
    toggleLock: () => {
      const isLocked = get().isLocked;
      if (isLocked) {
        handleUnlock();
      } else {
        handleLock();
      }
    },
  };
});
